import { Lightbulb } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { Thought } from './thoughts';

interface ThoughtForTodayProps {
  thought: Thought;
  /** The break sheet's smaller version: no frame, smaller quote. */
  compact?: boolean;
  /** The hub's version: the fact as a plain muted line, no tinted box. */
  quiet?: boolean;
  className?: string;
}

/** A kind line and a study-science fact. Pure: the day is the caller's. */
export function ThoughtForToday({
  thought,
  compact = false,
  quiet = false,
  className,
}: ThoughtForTodayProps) {
  return (
    <figure className={cn('space-y-2', className)}>
      <blockquote
        className={cn(
          'font-medium leading-snug text-foreground',
          compact ? 'text-sm' : 'text-lg'
        )}
      >
        “{thought.quote}”
      </blockquote>
      {thought.author && (
        <figcaption className='font-mono text-xs text-muted-foreground'>
          {thought.author}
        </figcaption>
      )}
      <p
        className={cn(
          'flex items-start gap-2 text-sm',
          quiet ? 'text-muted-foreground' : 'tint-brand rounded-lg px-3 py-2'
        )}
      >
        <Lightbulb className='mt-0.5 h-4 w-4 shrink-0' aria-hidden='true' />
        <span>{thought.fact}</span>
      </p>
    </figure>
  );
}

/** Holds the thought's space while it loads, so nothing below it jumps. */
export function ThoughtSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('space-y-3', className)}
      role='status'
      aria-label='Loading the thought for today'
    >
      <div className='h-5 w-4/5 animate-pulse rounded bg-muted' />
      <div className='h-3 w-24 animate-pulse rounded bg-muted' />
      <div className='h-4 w-3/5 animate-pulse rounded bg-muted' />
    </div>
  );
}
